import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { format, isValid, parse } from "date-fns";

export type OrderTab = "draft" | "manual-attention" | "missing-imei" | "missing-signing" | "completed";
export type ManualAttentionType = "INTERNAL_MANUAL_ATTENTION_REQUIRED" | "MANUAL_ATTENTION_REQUIRED_BY_DEALER";

const ORDER_TABS: readonly OrderTab[] = ["draft", "manual-attention", "missing-imei", "missing-signing", "completed"];
const MANUAL_ATTENTION_TYPES: readonly ManualAttentionType[] = [
    "INTERNAL_MANUAL_ATTENTION_REQUIRED",
    "MANUAL_ATTENTION_REQUIRED_BY_DEALER",
];
const DEFAULT_TAB: OrderTab = "completed";
const DEFAULT_MANUAL_ATTENTION_TYPE: ManualAttentionType = "MANUAL_ATTENTION_REQUIRED_BY_DEALER";
const DATE_FORMAT = "yyyy-MM-dd";

export const isOrderTab = (value: string): value is OrderTab => (ORDER_TABS as readonly string[]).includes(value);

const isManualAttentionType = (value: string): value is ManualAttentionType =>
    (MANUAL_ATTENTION_TYPES as readonly string[]).includes(value);

const parseDate = (value: string | null): Date | null => {
    if (!value) return null;
    const date = parse(value, DATE_FORMAT, new Date());
    return isValid(date) ? date : null;
};

export const encodeSearchParam = (value: string): string => {
    const binary = String.fromCharCode(...new TextEncoder().encode(value));
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export const decodeSearchParam = (value: string | null): string => {
    if (!value) return "";
    try {
        const binary = atob(value.replace(/-/g, "+").replace(/_/g, "/"));
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    } catch {
        return "";
    }
};

export interface OrdersFilters {
    activeTab: OrderTab;
    agentId: string;
    endDate: Date | null;
    manualAttentionType: ManualAttentionType;
    pageIndex: number;
    search: string;
    startDate: Date | null;
}

export const parseOrdersFilterParams = (searchParams: URLSearchParams): OrdersFilters => {
    const tab = searchParams.get("tab") ?? "";
    const type = searchParams.get("type") ?? "";
    const page = Number(searchParams.get("page"));

    return {
        activeTab: isOrderTab(tab) ? tab : DEFAULT_TAB,
        agentId: searchParams.get("agent") ?? "",
        endDate: parseDate(searchParams.get("to")),
        manualAttentionType: isManualAttentionType(type) ? type : DEFAULT_MANUAL_ATTENTION_TYPE,
        pageIndex: Number.isInteger(page) && page >= 1 ? page - 1 : 0,
        search: decodeSearchParam(searchParams.get("q")),
        startDate: parseDate(searchParams.get("from")),
    };
};

const useOrdersFilterParams = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = useMemo(() => parseOrdersFilterParams(searchParams), [searchParams]);

    const updateParams = useCallback(
        (updates: Record<string, string | null>, resetPage = true) => {
            setSearchParams(
                (prev) => {
                    const hasChanges = Object.entries(updates).some(
                        ([key, value]) => (prev.get(key) ?? null) !== (value || null),
                    );
                    if (!hasChanges) return prev;

                    const next = new URLSearchParams(prev);
                    if (resetPage) next.delete("page");
                    for (const [key, value] of Object.entries(updates)) {
                        if (value) {
                            next.set(key, value);
                        } else {
                            next.delete(key);
                        }
                    }
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    const setActiveTab = useCallback(
        (tab: OrderTab) => updateParams({ tab: tab === DEFAULT_TAB ? null : tab }),
        [updateParams],
    );

    const setAgentId = useCallback((agentId: string) => updateParams({ agent: agentId || null }), [updateParams]);

    const setDateRange = useCallback(
        ([from, to]: [Date | null, Date | null]) =>
            updateParams({
                from: from ? format(from, DATE_FORMAT) : null,
                to: to ? format(to, DATE_FORMAT) : null,
            }),
        [updateParams],
    );

    const setSearch = useCallback(
        (search: string) => updateParams({ q: search ? encodeSearchParam(search) : null }),
        [updateParams],
    );

    const setPageIndex = useCallback(
        (pageIndex: number) => updateParams({ page: pageIndex > 0 ? String(pageIndex + 1) : null }, false),
        [updateParams],
    );

    const setManualAttentionType = useCallback(
        (type: ManualAttentionType) => updateParams({ type: type === DEFAULT_MANUAL_ATTENTION_TYPE ? null : type }),
        [updateParams],
    );

    return {
        ...filters,
        setActiveTab,
        setAgentId,
        setDateRange,
        setManualAttentionType,
        setPageIndex,
        setSearch,
    };
};

export default useOrdersFilterParams;
